/**
 * Shared Rebalancer styles — substantial repeated blocks only.
 * Keep one-off / small sx (buttons, titles, icons) inline in components.
 */

// ── Review Data dialog sizing ─────────────────────────────────
export const REVIEW_CARD_WIDTH = 500;
export const CARD_GAP = 8;
export const DIALOG_INSET = 72;

export const getReviewDataDialogWidth = (tableCount) => {
  if (tableCount <= 0) return 480;
  const cardsWidth =
    tableCount * REVIEW_CARD_WIDTH + Math.max(0, tableCount - 1) * CARD_GAP;
  const total = cardsWidth + DIALOG_INSET;
  const viewportCap =
    typeof window !== "undefined" ? window.innerWidth * 0.95 : 1650;
  return Math.min(total, viewportCap, 1650);
};

export const getReviewDataDialogPaperSx = (width) => ({
  "& .MuiDialog-paper": {
    width,
    maxWidth: "95vw",
    height: "auto",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    borderRadius: 2,
    margin: 2,
  },
});

// ── Page shell (Rebalancer.js) ────────────────────────────────
export const getRebalancerPageSx = (theme) => ({
  backgroundColor:
    theme.palette.mode == "dark" ? theme.palette.grey[50] : "#ebebeb",
  background:
    theme.palette.mode == "dark" ? theme.palette.grey[50] : "#ebebeb",
  flex: 1,
  alignSelf: "stretch",
  width: "100%",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  pt: 3,
  pb: 2,
  boxSizing: "border-box",
});

export const getRebalancerMainCardSx = (theme) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  borderRadius: 2,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: `1px solid ${theme.palette.divider}`,
  mb: 0,
});

export const rebalancerCardInnerContainerSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  mt: 2,
  mb: 0,
  pb: 2,
};

export const rebalancerStepContentWrapperSx = {
  width: "100%",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// ── Step layout shells (FundStep / SwapStep) ────────────────
export const rebalancerStepContainerSx = {
  marginTop: 2,
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  width: "100%",
};

export const rebalancerStepContainerWithBottomMarginSx = {
  ...rebalancerStepContainerSx,
  marginBottom: 2,
};

export const rebalancerStepInnerColumnSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  gap: 2,
};

export const rebalancerFillColumnSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const rebalancerBlacklistedTableRowSx = {
  display: "flex",
  flexDirection: "row",
  width: "60%",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

export const rebalancerLocatesThreeColumnRowSx = {
  display: "flex",
  flexDirection: "row",
  width: "70%",
  flex: 1,
  minHeight: 0,
  gap: 2,
  overflow: "hidden",
};

// ── Locates / blacklist table panels ──────────────────────────
export const rebalancerTablePanelSx = {
  borderRadius: "4px",
  marginTop: "1px",
  width: "100%",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const getRebalancerScrollableTableContainerSx = (theme) => ({
  flex: "1 1 0px",
  minHeight: 0,
  height: 0,
  maxHeight: "100%",
  overflow: "auto",
  display: "block",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
});

export const getRebalancerLocatesTableRowSx = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    background: theme.palette.action.hover,
    cursor: "pointer",
  },
});

export const getRebalancerLocatesTableCellSx = (theme, align = "left") => ({
  fontSize: "12px",
  paddingLeft: align === "left" ? "10px" : undefined,
  paddingRight: align === "right" ? "10px" : undefined,
  borderLeft: "none",
  borderRight: "none",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderColor: theme.palette.divider,
});

// ── Review Data cards (ReviewData.js / SwapRebalanceInfo.js) ─
export const getRebalancerReviewOuterSx = (theme, showNext) => ({
  border: showNext ? `1px solid ${theme.palette.divider}` : "none",
  borderRadius: "4px",
  width: showNext ? "100%" : "fit-content",
  maxWidth: "100%",
  marginRight: showNext ? 1 : 0,
  flex: showNext ? 1 : undefined,
  minHeight: showNext ? 0 : "auto",
  minWidth: showNext ? 0 : undefined,
  overflow: showNext ? "hidden" : undefined,
  display: "flex",
  flexDirection: "column",
});

export const getRebalancerHorizontalScrollRowSx = (showNext) => ({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexWrap: "nowrap",
  overflowX: "auto",
  overflowY: "hidden",
  gap: 1,
  px: showNext ? 2 : 0,
  pb: 1,
  pr: showNext ? 3 : 0,
  boxSizing: "border-box",
  ...(showNext && {
    flex: "1 1 0px",
    minHeight: 0,
    alignItems: "stretch",
  }),
});

export const getRebalancerReviewCardSx = ({ width, showNext }) => ({
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "10px",
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width,
  flexShrink: 0,
  ...(showNext && {
    alignSelf: "stretch",
    height: "100%",
    maxHeight: "100%",
    minHeight: 0,
    overflow: "hidden",
  }),
  position: "relative",
});

export const getRebalancerSummaryTableSx = (showNext) => ({
  borderCollapse: "collapse",
  border: "1px solid #7C7C7C",
  paddingBottom: 1,
  ...(showNext && { flexShrink: 0 }),
});

export const getRebalancerSummaryHeaderRowSx = (theme) => ({
  backgroundColor: theme.palette.grey[100],
});

export const getRebalancerSummaryHeaderCellSx = (theme) => ({
  fontSize: "12px",
  border: "none",
  fontWeight: "bold",
  paddingBottom: "10px",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.grey[100],
});

export const getRebalancerSummaryHeaderCellLeftSx = (theme) => ({
  ...getRebalancerSummaryHeaderCellSx(theme),
  paddingLeft: "12px",
});

export const getRebalancerReviewTableFillSx = (showNext) =>
  showNext
    ? {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }
    : undefined;

// ── Validated-data popup tables ───────────────────────────────
export const getValidatedTableStyles = (theme) => ({
  section: {
    width: "100%",
    minWidth: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    overflow: "hidden",
  },
  title: {
    fontWeight: "bold",
    fontSize: "16px",
    px: 1.5,
    py: 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableContainer: {
    height: "auto",
    maxHeight: "300px",
    minHeight: "50px",
  },
  table: { borderCollapse: "collapse" },
  row: {
    "&:last-child td": { borderBottom: "none" },
  },
});

export const getCellStyle = (theme, align = "left") => ({
  fontSize: "12px",
  paddingLeft: align === "left" ? "10px" : undefined,
  paddingRight: "10px",
  borderLeft: "none",
  borderRight: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
});
